/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSilentFail,
  ActionSuccess,
  Asset,
  Ducks,
  MenuItem,
  MenuItemType,
  MenuUiArea,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, CallEffect, fork, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';

import Constants from 'configs/Constants';
import * as Assets from 'core/store/ducks/assets';
import { StoreState } from 'store/store';
import { Rail, RailType } from 'types/Rail';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';

const ADDITIONAL_ASSETS_TO_REQUEST = 2;
const BADGES_PRIORITY = BADGES_PRIORITY_PER_SECTION_RAIL.ONDEMAND.RAILS;

interface NextLevelPageMetaData {
  menuItemId?: number;
  categoryItemId?: number;
  withCategories?: boolean;
  channelId?: number;
}

enum ActionType {
  INIT = 'app/onDemand/INIT',
  INIT_NEXT_LEVEL = 'app/onDemand/INIT_NEXT_LEVEL',
  FINISH_PROCESSING = 'app/onDemand/FINISH_PROCESSING',
  ADD_SECTION_ASSET = 'app/onDemand/ADD_SECTION_ASSET',
  SECTION_ASSET_FAILED = 'app/onDemand/SECTION_ASSET_FAILED',
  ADD_RAIL = 'app/onDemand/ADD_RAIL',
  RAIL_LOAD_COMPLETED = 'app/onDemand/RAIL_LOAD_COMPLETED',
  ADD_CATEGORY_RAIL = 'app/onDemand/ADD_CATEGORY_RAIL',
  CATEGORY_RAIL_LOAD_COMPLETED = 'app/onDemand/CATEGORY_RAIL_LOAD_COMPLETED',
  LOAD_GRID_ASSETS = 'app/onDemand/LOAD_GRID_ASSETS',
  ADD_REQUESTED_GRID_PAGE = 'app/onDemand/ADD_REQUESTED_GRID_PAGE',
}

type Init = ActionRequest<ActionType.INIT>;
type InitNextLevel = ActionRequest<ActionType.INIT_NEXT_LEVEL, NextLevelPageMetaData>;
type AddSectionAsset = ActionRequest<ActionType.ADD_SECTION_ASSET, Asset>;
type SectionAssetFailed = ActionSilentFail<ActionType.SECTION_ASSET_FAILED>;
type AddRail = ActionRequest<ActionType.ADD_RAIL, Rail>;
type RailLoadCompleted = ActionSuccess<ActionType.RAIL_LOAD_COMPLETED, { id: number; assets: Asset[] }>;
type AddCategoryRail = ActionRequest<ActionType.ADD_CATEGORY_RAIL, Rail>;
type CategoryRailLoadCompleted = ActionSuccess<
  ActionType.CATEGORY_RAIL_LOAD_COMPLETED,
  { id: number; assets: Asset[] }
>;
type LoadGridAssets = ActionRequest<
  ActionType.LOAD_GRID_ASSETS,
  { id: number; recommContext?: Ducks.Recommendations.RecommContext }
>;
type AddRequestedGridPage = ActionSuccess<ActionType.ADD_REQUESTED_GRID_PAGE, number>;
type FinishProcessing = ActionRequest<ActionType.FINISH_PROCESSING>;

type Actions =
  | Init
  | InitNextLevel
  | AddSectionAsset
  | SectionAssetFailed
  | AddRail
  | RailLoadCompleted
  | AddCategoryRail
  | CategoryRailLoadCompleted
  | LoadGridAssets
  | AddRequestedGridPage
  | FinishProcessing;

const Actions = {
  init: (): Init => ({ type: ActionType.INIT }),
  initNextLevel: (meta: NextLevelPageMetaData): InitNextLevel => ({ type: ActionType.INIT_NEXT_LEVEL, meta }),
  addSectionAsset: (asset: Asset): AddSectionAsset => ({ type: ActionType.ADD_SECTION_ASSET, meta: asset }),
  sectionAssetFailed: (): SectionAssetFailed => ({ type: ActionType.SECTION_ASSET_FAILED }),
  addRail: (rail: Rail): AddRail => ({ type: ActionType.ADD_RAIL, meta: rail }),
  railLoadCompleted: (id: number, assets: Asset[]): RailLoadCompleted => ({
    type: ActionType.RAIL_LOAD_COMPLETED,
    payload: { id, assets },
  }),
  addCategoryRail: (rail: Rail): AddCategoryRail => ({ type: ActionType.ADD_CATEGORY_RAIL, meta: rail }),
  categoryRailLoadCompleted: (id: number, assets: Asset[]): CategoryRailLoadCompleted => ({
    type: ActionType.CATEGORY_RAIL_LOAD_COMPLETED,
    payload: { id, assets },
  }),
  loadGridAssets: (id: number, recommContext?: Ducks.Recommendations.RecommContext): LoadGridAssets => ({
    type: ActionType.LOAD_GRID_ASSETS,
    meta: { id, recommContext },
  }),
  addRequestedGridPage: (requestedPages: number): AddRequestedGridPage => ({
    type: ActionType.ADD_REQUESTED_GRID_PAGE,
    payload: requestedPages,
  }),
  finishProcessing: (): FinishProcessing => ({ type: ActionType.FINISH_PROCESSING }),
};

interface RailsActionsCallback {
  request: typeof Actions.addRail | typeof Actions.addCategoryRail;
  complete: typeof Actions.railLoadCompleted | typeof Actions.categoryRailLoadCompleted;
}

export const ActionCreators = {
  init: Actions.init,
  initNextLevel: Actions.initNextLevel,
  loadGridAssets: Actions.loadGridAssets,
};

type ReducerState = {
  rails: Rail[];
  categoryRails: Rail[];
  sectionAsset?: Asset;
  processing: boolean;
  processed: boolean;
  requestedGridPage: number;
  processingRequestedGridPage: boolean;
  error?: XError;
};

const initialState: ReducerState = {
  rails: [],
  categoryRails: [],
  processed: false,
  processing: false,
  requestedGridPage: 0,
  processingRequestedGridPage: false,
};

export default function reducer(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT: {
      return {
        ...state,
        rails: [],
        processing: true,
        processed: false,
        error: undefined,
      };
    }
    case ActionType.INIT_NEXT_LEVEL: {
      return {
        ...state,
        categoryRails: [],
        requestedGridPage: 0,
        processing: true,
        processed: false,
        error: undefined,
      };
    }
    case ActionType.ADD_SECTION_ASSET: {
      return {
        ...state,
        sectionAsset: action.meta,
        processing: false,
        processed: true,
        error: undefined,
      };
    }
    case ActionType.SECTION_ASSET_FAILED: {
      return {
        ...state,
        processed: true,
      };
    }
    case ActionType.ADD_RAIL: {
      return {
        ...state,
        rails: [...state.rails, action.meta],
      };
    }
    case ActionType.RAIL_LOAD_COMPLETED: {
      return {
        ...state,
        rails: state.rails.map((rail) => {
          if (rail.id !== action.payload.id) {
            return rail;
          }

          return {
            ...rail,
            loadingAssets: false,
            assets: action.payload.assets,
            hasMore: action.payload.assets.length > Constants.MAX_ASSETS_PER_RAIL || rail.withCategories,
          };
        }),
      };
    }
    case ActionType.ADD_CATEGORY_RAIL: {
      return {
        ...state,
        categoryRails: [...state.categoryRails, action.meta],
      };
    }
    case ActionType.CATEGORY_RAIL_LOAD_COMPLETED: {
      return {
        ...state,
        categoryRails: state.categoryRails.map((rail) => {
          if (rail.id !== action.payload.id) {
            return rail;
          }

          return {
            ...rail,
            loadingAssets: false,
            assets: action.payload.assets,
            hasMore: action.payload.assets.length > Constants.MAX_ASSETS_PER_RAIL,
          };
        }),
      };
    }
    case ActionType.LOAD_GRID_ASSETS: {
      return {
        ...state,
        processingRequestedGridPage: true,
      };
    }
    case ActionType.ADD_REQUESTED_GRID_PAGE: {
      return {
        ...state,
        processed: state.processed || true,
        processing: state.processing && false,
        processingRequestedGridPage: false,
        requestedGridPage: state.requestedGridPage + action.payload,
      };
    }
    case ActionType.FINISH_PROCESSING: {
      return {
        ...state,
        processed: true,
        processing: false,
      };
    }
    default:
      return state;
  }
}

export const Selectors = {
  isProcessed: (store: StoreState) => store.onDemand.processed === true,
  getError: (store: StoreState) => store.onDemand.error,
  getSectionAsset: (store: StoreState) => store.onDemand.sectionAsset,
  getRails: (store: StoreState) => store.onDemand.rails,
  getCategoryRails: (store: StoreState) => store.onDemand.categoryRails,
  getRail: (store: StoreState, id?: number) => (id ? store.onDemand.rails.find((rail) => rail.id === id) : undefined),
  getCategoryRail: (store: StoreState, id?: number) =>
    id ? store.onDemand.categoryRails.find((rail) => rail.id === id) : undefined,
  getRequestedPageNumber: (store: StoreState) => store.onDemand.requestedGridPage,
  processingRequestedGridPage: (store: StoreState) => store.onDemand.processingRequestedGridPage,
};

// AUX
function takeAssets(actionType: Assets.ActionTypes.SUCCESS | Assets.ActionTypes.FAIL, channelId: number): any {
  return take((act: any) => act.type === actionType && act.meta.id === channelId);
}

function* setSectionAsset(asset?: Asset): SagaIterator {
  if (asset) {
    yield put(Actions.addSectionAsset(asset));
    return;
  }

  yield put(Actions.sectionAssetFailed());
}

function* getAssets(
  item: MenuItem,
  assetsToRequest: number,
  recommContext?: Ducks.Recommendations.RecommContext
): SagaIterator<Asset[] | undefined> {
  if (!recommContext) {
    if (!item.channelId) {
      return [];
    }

    yield put(Assets.Creators.loadAssets(item.channelId, 0, assetsToRequest));

    yield race([
      takeAssets(Assets.ActionTypes.SUCCESS, item.channelId),
      takeAssets(Assets.ActionTypes.FAIL, item.channelId),
    ]);

    return yield select(Assets.Selectors.getAssets, item.channelId);
  }

  yield put(Ducks.Recommendations.Creators.loadAssets(recommContext));

  yield race([
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.SUCCESS, recommContext),
    Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.FAIL, recommContext),
  ]);

  return yield select(Ducks.Recommendations.Selectors.assets, recommContext);
}

function* getRailAssets(
  item: MenuItem,
  actionsCallback: RailsActionsCallback,
  isFirstRailInMainPage?: boolean,
  recommContext?: Ducks.Recommendations.RecommContext
): SagaIterator<void> {
  if (!item.channelId && !recommContext) {
    return;
  }

  const assetsToRequest = Constants.MAX_ASSETS_PER_RAIL + ADDITIONAL_ASSETS_TO_REQUEST;

  yield put(
    actionsCallback.request({
      id: item.id,
      title: item.name,
      type: recommContext && RailType.RECOMMENDATIONS,
      channelId: item.channelId,
      assets: [],
      loadingAssets: true,
      withCategories: item.children?.length > 0,
    })
  );

  let railAssets: Asset[] = (yield call(getAssets, item, assetsToRequest, recommContext)) || [];

  if (isFirstRailInMainPage && railAssets?.length) {
    yield call(setSectionAsset, railAssets[0]);

    railAssets = railAssets.slice(1, assetsToRequest);
  }

  const assetsWithBadges: Asset[] = yield call(
    RailBadgeEngine.getAssetsWithBadgesListFromRail,
    railAssets,
    BADGES_PRIORITY
  );

  yield put(actionsCallback.complete(item.id, assetsWithBadges));
}

function* railFactory(
  item: MenuItem,
  actionsCallback: RailsActionsCallback,
  isFirstRailInMainPage?: boolean
): SagaIterator<CallEffect | undefined> {
  const isRecommEnabled: boolean = yield select(Ducks.Recommendations.Selectors.isFeatureEnabled);

  // TODO Implement the KSQL and External rails types.
  switch (item.type) {
    case MenuItemType.KALTURA:
      return yield call(getRailAssets, item, actionsCallback, isFirstRailInMainPage);
    case MenuItemType.KSQL:
      return undefined;
    case MenuItemType.EXTERNAL: {
      if (!isRecommEnabled) {
        return undefined;
      }
      return yield call(getRailAssets, item, actionsCallback, isFirstRailInMainPage, {
        menuUiArea: MenuUiArea.VOD,
        menuItemId: item.id,
      });
    }
    default:
      return undefined;
  }
}

function* loadGridAssets(action: LoadGridAssets): SagaIterator {
  const { id, recommContext } = action.meta;
  const pageIndexToRequest = yield select(Selectors.getRequestedPageNumber);

  if (recommContext) {
    yield put(Ducks.Recommendations.Creators.loadAssets(recommContext, true));

    yield race([
      Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.SUCCESS, recommContext),
      Ducks.Recommendations.takeAssets(Ducks.Recommendations.ActionType.FAIL, recommContext),
    ]);
  } else {
    yield put(Assets.Creators.loadAssets(id, pageIndexToRequest, Constants.ASSETS_PER_GRID_PAGE));

    yield race([takeAssets(Assets.ActionTypes.SUCCESS, id), takeAssets(Assets.ActionTypes.FAIL, id)]);
  }

  yield put(Actions.addRequestedGridPage(1));
}

function* initGrid(recommContext?: Ducks.Recommendations.RecommContext, channelId?: number): SagaIterator {
  if (recommContext) {
    yield put(Actions.loadGridAssets(recommContext.menuItemId, recommContext));
    return;
  }

  if (!channelId) {
    return;
  }

  yield put(Actions.loadGridAssets(channelId));
}

function* initRails(menuItem: MenuItem, categoryItemId?: number): SagaIterator<Rail> {
  let parentRail: Rail = yield select(Selectors.getRail, menuItem.id);
  if (!parentRail) {
    parentRail = {
      id: menuItem.id,
      title: menuItem.name,
      type: menuItem.type === MenuItemType.EXTERNAL ? RailType.RECOMMENDATIONS : undefined,
      channelId: menuItem.channelId,
      assets: [],
      withCategories: menuItem.children?.length > 0,
    };
    yield put(Actions.addRail(parentRail));
  }

  if (categoryItemId) {
    parentRail = yield select(Selectors.getCategoryRail, categoryItemId);
    const categoryItem: MenuItem | undefined = menuItem?.children.find((el) => el.id === categoryItemId);

    if (!parentRail && categoryItem) {
      parentRail = {
        id: categoryItem.id,
        title: categoryItem.name,
        type: categoryItem.type === MenuItemType.EXTERNAL ? RailType.RECOMMENDATIONS : undefined,
        channelId: categoryItem.channelId,
        assets: [],
        hasMore: true,
      };
      yield put(Actions.addCategoryRail(parentRail));
    }
  }

  yield put(Actions.finishProcessing());

  return parentRail;
}

function* initNextLevel(action: InitNextLevel): SagaIterator {
  const { menuItemId, categoryItemId, withCategories, channelId } = action.meta;

  const mainMenuItem: MenuItem | undefined = yield select(Ducks.Menu.Selectors.getMainMenuItem, MenuUiArea.VOD);

  if (!mainMenuItem || menuItemId === undefined) {
    yield put(Actions.finishProcessing());
    return;
  }

  const menuItem: MenuItem | undefined = mainMenuItem?.children.find((el) => el.id === menuItemId);
  if (!menuItem) {
    yield put(Actions.finishProcessing());
    return;
  }

  const parentRailInitialized: Rail = yield call(initRails, menuItem, categoryItemId);

  if (!withCategories) {
    const gridMenuItemChannelId = channelId || parentRailInitialized.channelId;

    yield call(
      initGrid,
      parentRailInitialized.type === RailType.RECOMMENDATIONS
        ? { menuUiArea: MenuUiArea.VOD, menuItemId: parentRailInitialized.id }
        : undefined,
      gridMenuItemChannelId
    );
    return;
  }

  const actionsCallback: RailsActionsCallback = {
    request: Actions.addCategoryRail,
    complete: Actions.categoryRailLoadCompleted,
  };

  for (let index = 0; index < (menuItem?.children.length || 0); index += 1) {
    yield fork(railFactory, menuItem.children[index], actionsCallback);
  }
}

function* init(): SagaIterator {
  const menuItem: MenuItem | undefined = yield select(Ducks.Menu.Selectors.getMainMenuItem, MenuUiArea.VOD);

  if (!menuItem) {
    return;
  }

  for (let index = 0; index < (menuItem?.children.length || 0); index += 1) {
    const actionsCallback: RailsActionsCallback = {
      request: Actions.addRail,
      complete: Actions.railLoadCompleted,
    };

    yield fork(railFactory, menuItem.children[index], actionsCallback, index === 0);
  }
}

export function* saga(): SagaIterator {
  yield all([
    takeEvery(ActionType.INIT, init),
    takeEvery(ActionType.INIT_NEXT_LEVEL, initNextLevel),
    takeLeading(ActionType.LOAD_GRID_ASSETS, loadGridAssets),
  ]);
}
